import React from "react";
import CertificateFooter from "./CertificateFooter";
import ThoroughExaminationDocument from "./ThoroughExaminationDocument";
import Fm200Document from "./Fm200Document";
import EmissionTestDocument from "./EmissionTestDocument";
import LiftingAccessoriesDocument from "./LiftingAccessoriesDocument";
import WastewaterEffluentDocument from "./WastewaterEffluentDocument";
import CraneOperatorDocument from "./CraneOperatorDocument";

const formatReportDate = (value) => {
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const renderRich = (text) =>
  String(text || "")
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((part, index) =>
      part.startsWith("**") && part.endsWith("**")
        ? <b key={index}>{part.slice(2, -2)}</b>
        : <React.Fragment key={index}>{part}</React.Fragment>
    );

// Preview dispatcher. Aluminum Truss is the default body; other templates
// swap in their own document component while sharing footer markup.
const CertificateDocument = ({ doc }) => {
  if (!doc) return null;
  if (doc.template_id === "thorough_examination_crane") {
    return <ThoroughExaminationDocument doc={doc} />;
  }
  if (doc.template_id === "fm200_gas_cylinder") {
    return <Fm200Document doc={doc} />;
  }
  if (doc.template_id === "emission_test_diesel_generator") {
    return <EmissionTestDocument doc={doc} />;
  }
  if (doc.template_id === "lifting_accessories_register") {
    return <LiftingAccessoriesDocument doc={doc} />;
  }
  if (doc.template_id === "wastewater_effluent_quality") {
    return <WastewaterEffluentDocument doc={doc} />;
  }
  if (doc.template_id === "crane_operator_card") {
    return <CraneOperatorDocument doc={doc} />;
  }

  const assetsBase = doc.assetsBase || "";
  const logos = doc.footerLogos || [];

  return (
    <div className="cc-pdf-page">
      <div className="cc-pdf-header">
        <img className="cc-pdf-logo" src={`${assetsBase}/logo.jpg`} alt="TUV PAK" />
        <div className="cc-pdf-header-text">
          <strong>TUV PAK INSPEC</strong>
          <span>TECHANICAL UNIQUE VISION PAKISTAN INSPECTION &amp; CERTIFICATION</span>
        </div>
      </div>
      <img className="cc-pdf-rule" src={`${assetsBase}/header-bar.png`} alt="" />

      <h4 className="cc-pdf-title">{doc.report_title}</h4>

      <table className="cc-pdf-meta">
        <tbody>
          <tr>
            <td><b>Project:</b> {doc.project}</td>
            <td><b>Report No.:</b> {doc.report_no}</td>
            <td><b>Location:</b> {doc.location}</td>
            <td><b>Date:</b> {formatReportDate(doc.report_date)}</td>
          </tr>
          <tr>
            <td><b>Client:</b> {doc.client}</td>
            <td><b>Contractor:</b> {doc.contractor}</td>
            <td><b>Structure:</b> {doc.structure}</td>
            <td><b>Rev.:</b> {doc.revision}</td>
          </tr>
        </tbody>
      </table>

      <div className="cc-pdf-band">PURPOSE &amp; SCOPE</div>
      <p className="cc-pdf-para">{renderRich(doc.purpose_scope)}</p>

      <div className="cc-pdf-band">PROPOSED SUSPENSION ARRANGEMENT</div>
      <p className="cc-pdf-para">{renderRich(doc.proposed_suspension_arrangement)}</p>

      <div className="cc-pdf-band">LOAD CALCULATION</div>
      <p className="cc-pdf-para">{renderRich(doc.load_calculation)}</p>

      <div className="cc-pdf-band">COMPONENT VERIFICATION</div>
      <table className="cc-pdf-table">
        <thead>
          <tr>
            <th>Component</th>
            <th>Assessment Item</th>
            <th>Requirement</th>
            <th>WLL/SWL ≥ Design Load</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {(doc.component_rows || []).map((row, index) => (
            <tr key={index}>
              <td>{row.component}</td>
              <td>{row.assessment_item}</td>
              <td>{row.requirement}</td>
              <td>{row.design_load}</td>
              <td>{row.status}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="cc-pdf-band cc-pdf-band--final">FINAL ASSESSMENT / CONCLUSION</div>
      <p className="cc-pdf-para cc-pdf-para--final">{renderRich(doc.final_assessment)}</p>

      <table className="cc-pdf-sign">
        <thead>
          <tr>
            <th>Inspected By</th>
            <th>Approved By</th>
            <th>Company Seal</th>
            <th>QR Code</th>
          </tr>
        </thead>
        <tbody>
          <tr className="cc-pdf-sign-names">
            <td>{doc.inspected_by_name}</td>
            <td>{doc.approved_by_name}</td>
            <td rowSpan={2} className="cc-pdf-sign-seal">
              {doc.sealUrl && <img src={doc.sealUrl} alt="Company seal" />}
            </td>
            <td rowSpan={2} className="cc-pdf-sign-qr">
              {doc.qrDataUrl && <img src={doc.qrDataUrl} alt="QR code" />}
            </td>
          </tr>
          <tr className="cc-pdf-sign-images">
            <td>{doc.inspectedSignatureUrl && <img src={doc.inspectedSignatureUrl} alt="Inspector signature" />}</td>
            <td>{doc.approvedSignatureUrl && <img src={doc.approvedSignatureUrl} alt="Approver signature" />}</td>
          </tr>
        </tbody>
      </table>

      <div className="cc-pdf-disclaimer">
        <p>{doc.disclaimer_text}</p>
        <b>{doc.end_of_report_text}</b>
      </div>

      <CertificateFooter
        assetsBase={assetsBase}
        logos={logos}
        website={doc.footer_website}
        email={doc.footer_email}
        gmail={doc.footer_gmail}
      />
    </div>
  );
};

export default CertificateDocument;
